import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAdminLocale } from '../../lib/admin-locale.js';
import { Button } from './Button.js';
import { Icon } from './Icon.js';
import { Modal } from './Modal.js';

export type ConfirmOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside ConfirmProvider');
  }
  return ctx;
}

export function ConfirmProvider(props: { children: ReactNode }) {
  const { text } = useAdminLocale();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const close = useCallback((value: boolean) => {
    setOpen(false);
    setOptions(null);
    resolver.current?.(value);
    resolver.current = null;
  }, []);

  const confirm = useCallback<ConfirmFn>((next) => {
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
      setOptions(next);
      setOpen(true);
    });
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {props.children}
      {open && options ? (
        <Modal
          title={options.title}
          onClose={() => close(false)}
          description={
            options.danger
              ? text('Η ενέργεια δεν αναιρείται.', 'This action cannot be undone.')
              : undefined
          }
        >
          <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-300">
            {options.message}
          </p>
          <div className="mt-6 flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => close(false)}>
              <Icon name="x" className="size-4" />
              {options.cancelLabel ?? text('Άκυρο', 'Cancel')}
            </Button>
            <Button
              type="button"
              variant={options.danger ? 'danger' : 'primary'}
              onClick={() => close(true)}
            >
              <Icon name={options.danger ? 'trash' : 'check'} className="size-4" />
              {options.confirmLabel ?? text('Επιβεβαίωση', 'Confirm')}
            </Button>
          </div>
        </Modal>
      ) : null}
    </ConfirmContext.Provider>
  );
}
